import { Injectable } from '@angular/core';
import { CONSTANTS } from 'src/app/core/constants/constants';
import { MESSAGES } from 'src/app/core/constants/messages';
import { Line } from 'src/app/core/models/line.model';
import { MatCable } from 'src/app/core/models/materials/mat-cable.model';
import { MatConnector } from 'src/app/core/models/materials/mat-connector.model';
import { MatFlexLine } from 'src/app/core/models/materials/mat-flex-line.model';
import { MatRigidTube } from 'src/app/core/models/materials/mat-rigid-tube.model';
import { MatStressJoint } from 'src/app/core/models/materials/mat-stress-joint.model';
import { Material } from 'src/app/core/models/materials/material.model';
import { ANNULUS_CONDITION } from 'src/app/core/models/results/annulus-condition';
import { FadFlexInstructions } from 'src/app/core/models/results/fad-flex/fad-flex-instructions.model';
import { FadFlexStructure } from 'src/app/core/models/results/fad-flex/fad-flex-structure.model';
import { FlexiblePipeLayer } from 'src/app/core/models/results/fad-flex/flexible-pipe-layer.model';
import { LineResultsRequester } from 'src/app/core/models/results/line-results-requester.model';
import { IntervalPipeType } from 'src/app/core/models/results/pipe-type.enum';
import { ResultInterval } from 'src/app/core/models/results/result-interval.model';
import { SteelPipeInstructions } from 'src/app/core/models/results/steel-pipe-instructions.model';
import { IsEqual } from 'src/app/shared/utils/is-equal';
import { DataSharingService } from '../../../data-sharing/data-sharing.service';
import { FeedbackService } from '../../../feedback/feedback.service';
import { MapsService } from '../../objects/maps/maps.service';
import { MaterialsService } from '../../objects/materials/materials.service';

interface MatchingPipes {
  flexible: Material[];
  rigid: MatRigidTube[];
  cable: MatCable[];
  endFitting: MatConnector[];
  stressJoint: MatStressJoint[];
}

interface EndByTypeOrUserInput {
  userMixedType: boolean;
  lastPoint: number;
  userInput: number;
  seletedType: MaterialType;
}

enum MaterialType {
  FLEXIBLE = 'flexible',
  RIGID = 'rigid',
}

interface SelectedPipes {
  selected: boolean;
  currentSelectedFlex: boolean;
  currentSelectedRigid: boolean;
  currentSelectedCable: boolean;
  currentSelectedEndFitting: boolean;
  currentSelectedStressJoint: boolean;
  currSegEnd: number;
  length: number;
  appliedFlexMaterial: Material;
  appliedRigidMaterial: Material;
  appliedCableMaterial: Material;
  appliedEndFittingMaterial: Material;
  appliedStressJointMaterial: Material;
}

interface TempSelection {
  selectedFlex: boolean;
  selectedRigid: boolean;
  selectedCable: boolean;
  selectedEndFitting: boolean;
  selectedStressJoint: boolean;
}

type ConnectorMatcher = (
  point: number,
  segStart: number,
  segEnd: number,
  intervalIndex: number,
) => boolean;

@Injectable({
  providedIn: 'root',
})
export class IntervalsService {
  constructor(
    private mapsService: MapsService,
    private materialsService: MaterialsService,
    private feedbackService: FeedbackService,
    private dataSharingService: DataSharingService,
  ) {}

  save(lineResultRequester: LineResultsRequester, payload: ResultInterval[]): void {
    let autoFixedIntervals = false;
    const updatedIntervals = payload.map((interval, index) => {
      const tempAutoFixedResult = this.autofixIntervalEndPoint(
        interval,
        index,
        lineResultRequester.lineId,
      );
      if (tempAutoFixedResult) {
        autoFixedIntervals = tempAutoFixedResult;
      }
      return this.savePipesInstructions(interval, lineResultRequester, index);
    });

    if (IsEqual(lineResultRequester.intervals, updatedIntervals)) {
      return;
    }

    lineResultRequester.intervals = updatedIntervals;

    if (autoFixedIntervals) {
      this.feedbackService.warning(MESSAGES.WARNING.REGION_AUTO_FIX_BY_LINE);
    }
    this.dataSharingService.localChanges$.next();
  }

  autofixIntervalEndPoint(interval: ResultInterval, index: number, lineId: number): boolean {
    if (interval.end < interval.beginning) {
      return false;
    }
    const line = this.mapsService.getObjectById(lineId);
    const result = this.getEndByTypeOrUserInput(interval.beginning, interval.end, line, index);
    const { userMixedType, lastPoint } = result;
    if (userMixedType) {
      interval.end = lastPoint;
      return true;
    }
    return false;
  }

  savePipesInstructions(
    interval: ResultInterval,
    lineResultRequester: LineResultsRequester,
    intervalIndex: number,
  ): ResultInterval {
    const { lineId } = lineResultRequester;
    const { beginning, end } = interval;
    const line: Line = this.mapsService.getObjectById(lineId);
    const matchingPipes = this.getSelectedPipes(beginning, end, line, intervalIndex);

    if (matchingPipes.flexible.length) {
      interval.fadFlexInstructions = this.saveFadFlexInstructions(matchingPipes, interval);
    } else {
      interval.fadFlexInstructions = null;
    }

    if (matchingPipes.rigid.length || matchingPipes.stressJoint.length) {
      interval.steelPipeInstructions = this.saveSteelPipeInstructions(matchingPipes, interval);
    } else {
      interval.steelPipeInstructions = null;
    }

    interval.type = this.getPipeTypeByMatchingPipes(matchingPipes);
    return interval;
  }

  getPipeTypeByMatchingPipes(matchingPipes: MatchingPipes): IntervalPipeType {
    if (matchingPipes.flexible.length) {
      return IntervalPipeType.FLEXIBLE;
    } else if (matchingPipes.rigid.length || matchingPipes.stressJoint.length) {
      return IntervalPipeType.RIGID;
    } else if (matchingPipes.cable.length) {
      return IntervalPipeType.CABLE;
    } else if (matchingPipes.endFitting.length) {
      return IntervalPipeType.END_FITTING;
    }
    return IntervalPipeType.UNKNOWN;
  }

  getPresetGlobalValues(interval: ResultInterval, strucIndex: number): any {
    if (!interval.fadFlexInstructions) {
      return null;
    }
    return {
      snCurveId: interval.fadFlexInstructions.snCurveId,
      anularCondition: interval.fadFlexInstructions.anularCondition,
      layerConfiguration: interval.fadFlexInstructions.structures[strucIndex]?.layerConfiguration,
    };
  }

  saveFadFlexInstructions(
    matchingPipes: MatchingPipes,
    interval: ResultInterval,
  ): FadFlexInstructions | null {
    if (!matchingPipes.flexible.length) {
      return null;
    }

    const fadFlexInstructions = interval.fadFlexInstructions;
    const newFadFlex = new FadFlexInstructions();

    if (fadFlexInstructions) {
      Object.assign(newFadFlex, fadFlexInstructions);
    }

    const uniquePipes = this.getUniquePipesByCofName(matchingPipes, newFadFlex);
    return this.getUpdatedFadFlex(interval, uniquePipes, fadFlexInstructions, newFadFlex);
  }

  getUniquePipesByCofName(
    matchingPipes: MatchingPipes,
    newFadFlex: FadFlexInstructions,
  ): Material[] {
    const uniquePipesByCofName = new Map<string, Material>();
    matchingPipes.flexible.forEach((pipe: MatFlexLine) => {
      uniquePipesByCofName.set(pipe.cofFileName, pipe);
    });
    newFadFlex.structures = [];
    return Array.from(uniquePipesByCofName.values());
  }

  resetInstructionsToDefault(instruction: FadFlexInstructions): void {
    instruction.snCurveId = -1;
    instruction.anularCondition = ANNULUS_CONDITION.DRY;
    instruction.structures = [];
  }

  getUpdatedFadFlex(
    interval: ResultInterval,
    uniquePipes: Material[],
    prevInstructions: FadFlexInstructions,
    newFadFlex: FadFlexInstructions,
  ): FadFlexInstructions {
    if (!uniquePipes.length) {
      return newFadFlex;
    }

    if (!prevInstructions) {
      prevInstructions = new FadFlexInstructions();
      interval.fadFlexInstructions = prevInstructions;
    }

    if (!prevInstructions) {
      this.resetInstructionsToDefault(newFadFlex);
    }

    let instructionCurvesEquals = true;
    let instructionAnularEquals = true;
    for (let index = 0; index < uniquePipes.length; index++) {
      const flexPipe = uniquePipes[index];
      const cofWasKept = flexPipe.cofFileName === prevInstructions?.structures[index]?.title;
      const prevStructureExists = !!prevInstructions?.structures[index];
      const prevCofWasKept = cofWasKept && prevStructureExists;

      if (!flexPipe.cofData) {
        continue;
      }

      const materialId = flexPipe.internalId || -1;
      const presetData = prevCofWasKept
        ? this.getDefaultStructure(flexPipe.cofFileName)
        : this.getPresetGlobalValues(interval, index);

      const oldStructure = prevCofWasKept ? prevInstructions?.structures?.[index] : presetData;
      let { snCurveId, anularCondition, layerConfiguration: oldLayerConfiguration } = oldStructure;
      const layerConfiguration = this.getLayerConfiguration(
        materialId,
        flexPipe,
        prevCofWasKept,
        oldLayerConfiguration,
      );

      newFadFlex.structures.push(
        new FadFlexStructure({
          id: null,
          title: flexPipe.cofFileName,
          layerConfiguration,
          snCurveId: prevCofWasKept ? snCurveId : -1,
          anularCondition: prevCofWasKept ? anularCondition : ANNULUS_CONDITION.DRY,
          materialId,
        }),
      );

      const firstStructure = newFadFlex.structures[0];
      const lastStructure = newFadFlex.structures[newFadFlex.structures.length - 1];
      if (firstStructure.snCurveId !== lastStructure.snCurveId) {
        instructionCurvesEquals = false;
      }
      if (firstStructure.anularCondition !== lastStructure.anularCondition) {
        instructionAnularEquals = false;
      }
    }

    if (instructionCurvesEquals) {
      newFadFlex.snCurveId = newFadFlex.structures[0]?.snCurveId || -1;
    } else {
      newFadFlex.snCurveId = -2;
    }

    if (instructionAnularEquals) {
      newFadFlex.anularCondition =
        newFadFlex.structures[0]?.anularCondition || ANNULUS_CONDITION.DRY;
    } else {
      newFadFlex.anularCondition = ANNULUS_CONDITION.MULTIPLE;
    }

    return newFadFlex;
  }

  getLayerConfiguration(
    materialId: number,
    flexPipe: Material,
    cofWasKept: boolean,
    oldLayerConfiguration: FlexiblePipeLayer[],
  ): FlexiblePipeLayer[] {
    return this.materialsService.getCofLayers(flexPipe).map(({ name }, layerIndex) => {
      if (cofWasKept && oldLayerConfiguration?.[layerIndex]) {
        return Object.assign(oldLayerConfiguration?.[layerIndex], {
          title: name,
          materialId,
          id: null,
        });
      }
      return this.getDefaultLayer(name);
    });
  }

  getDefaultStructure(title: string): FadFlexStructure {
    return {
      id: null,
      title,
      snCurveId: -1,
      materialId: -1,
      anularCondition: ANNULUS_CONDITION.DRY,
      layerConfiguration: [],
    };
  }

  getDefaultLayer(title: string): FlexiblePipeLayer {
    return {
      id: null,
      title,
      snCurveId: -1,
      materialId: -1,
      anularCondition: ANNULUS_CONDITION.DRY,
    };
  }

  saveSteelPipeInstructions(
    matchingPipes: MatchingPipes,
    interval: ResultInterval,
  ): SteelPipeInstructions | null {
    const steelPipes: Array<MatRigidTube | MatStressJoint> = [
      ...matchingPipes.rigid,
      ...matchingPipes.stressJoint,
    ];

    if (!steelPipes.length || !steelPipes[0]) {
      return null;
    }

    const { steelPipeInstructions } = interval;

    if (steelPipeInstructions) {
      return Object.assign(steelPipeInstructions, {
        materialId: steelPipes[0].internalId,
        label: interval?.label,
      });
    }

    return this.getDefaultSteelPipeInstructions(steelPipes[0].internalId, interval.label);
  }

  getDefaultSteelPipeInstructions(materialId: number, label: string): SteelPipeInstructions {
    return {
      materialId,
      label,
      innerStressConcentrationFactor: 1,
      outerStressConcentrationFactor: 1,
      snCurveId: -1,
      outerSnCurveId: -1,
      detailedResults: false,
      histogram: false,
    };
  }

  getEndByTypeOrUserInput(
    beginning: number,
    end: number,
    line: Line,
    intervalIndex: number,
  ): EndByTypeOrUserInput {
    let userMixedType = false;
    let userInput = end;
    let selectedFlex = false;
    let selectedRigid = false;
    let selectedCable = false;
    let selectedEndFitting = false;
    let selectedStressJoint = false;
    let currSegStart = 0;
    let lastPoint = beginning;

    for (let index = 0; index < line.segmentMesh.length; index++) {
      const selectedPipes = this.getSelectedPipesByMeshIndex(
        line,
        index,
        currSegStart,
        beginning,
        end,
        intervalIndex,
      );
      const {
        selected,
        currentSelectedFlex,
        currentSelectedRigid,
        currentSelectedCable,
        currentSelectedEndFitting,
        currentSelectedStressJoint,
        currSegEnd,
        length,
      } = selectedPipes;
      currSegStart += length;

      if (!selected) {
        continue;
      }

      userMixedType = this.checkMixedPipeTypes(selectedPipes, {
        selectedFlex,
        selectedRigid,
        selectedCable,
        selectedEndFitting,
        selectedStressJoint,
      });

      if (userMixedType) {
        return {
          userMixedType,
          lastPoint,
          userInput,
          seletedType: selectedFlex ? MaterialType.FLEXIBLE : MaterialType.RIGID,
        };
      }

      selectedCable = selectedCable || currentSelectedCable;
      selectedEndFitting = selectedEndFitting || currentSelectedEndFitting;
      selectedFlex = selectedFlex || currentSelectedFlex;
      selectedRigid =
        selectedRigid || currentSelectedRigid || selectedStressJoint || currentSelectedStressJoint;
      lastPoint = currSegEnd;
    }

    return {
      userMixedType,
      lastPoint,
      userInput,
      seletedType: selectedFlex ? MaterialType.FLEXIBLE : MaterialType.RIGID,
    };
  }

  checkMixedPipeTypes(selectedPipes: SelectedPipes, tempSelection: TempSelection): boolean {
    const {
      currentSelectedFlex,
      currentSelectedRigid,
      currentSelectedCable,
      currentSelectedEndFitting,
      currentSelectedStressJoint,
    } = selectedPipes;
    const { selectedFlex, selectedRigid, selectedCable, selectedEndFitting, selectedStressJoint } =
      tempSelection;
    return (
      (selectedFlex && selectedRigid) ||
      (selectedFlex && selectedCable) ||
      (selectedFlex && selectedEndFitting) ||
      (selectedFlex && selectedStressJoint) ||
      (selectedFlex && currentSelectedRigid) ||
      (selectedFlex && currentSelectedCable) ||
      (selectedFlex && currentSelectedEndFitting) ||
      (selectedFlex && currentSelectedStressJoint) ||
      (selectedRigid && currentSelectedFlex) ||
      (selectedRigid && currentSelectedCable) ||
      (selectedRigid && currentSelectedEndFitting) ||
      (selectedRigid && currentSelectedStressJoint) ||
      (selectedCable && currentSelectedFlex) ||
      (selectedCable && currentSelectedRigid) ||
      (selectedCable && currentSelectedEndFitting) ||
      (selectedCable && currentSelectedStressJoint) ||
      (selectedEndFitting && currentSelectedFlex) ||
      (selectedEndFitting && currentSelectedCable) ||
      (selectedEndFitting && currentSelectedRigid) ||
      (selectedStressJoint && currentSelectedFlex) ||
      (selectedStressJoint && currentSelectedCable) ||
      (selectedStressJoint && currentSelectedRigid)
    );
  }

  getSelectedPipesByMeshIndex(
    line: Line,
    index: number,
    currSegStart: number,
    beg: number,
    end: number,
    intervalIndex: number,
    isConnector = false,
    connectorVersusSegmentOverlap?: ConnectorMatcher,
  ): SelectedPipes {
    const length = line.segmentMesh[index].l;
    const currSegEnd = length + currSegStart;

    let selected: boolean;

    if (isConnector) {
      selected = connectorVersusSegmentOverlap(beg, currSegStart, currSegEnd, intervalIndex);
    } else {
      selected = this.intervalVersusSegmentOverlap(
        beg,
        end,
        currSegStart,
        currSegEnd,
        intervalIndex,
      );
    }

    const appliedFlexMaterial = this.checkFlexTypeBySegIndex(index, line);
    const appliedRigidMaterial = this.checkRigidTypeBySegIndex(index, line);
    const appliedCableMaterial = this.checkCableTypeBySegIndex(index, line);
    const appliedEndFittingMaterial = this.checkEndFittingTypeBySegIndex(index, line);
    const appliedStressJointMaterial = this.checkStressJointTypeBySegIndex(index, line);
    const currentSelectedFlex = selected && !!appliedFlexMaterial;
    const currentSelectedRigid = selected && !!appliedRigidMaterial;
    const currentSelectedCable = selected && !!appliedCableMaterial;
    const currentSelectedEndFitting = selected && !!appliedEndFittingMaterial;
    const currentSelectedStressJoint = selected && !!appliedEndFittingMaterial;

    return {
      selected,
      currentSelectedFlex,
      currentSelectedRigid,
      currentSelectedCable,
      currentSelectedEndFitting,
      currentSelectedStressJoint,
      currSegEnd,
      length,
      appliedFlexMaterial,
      appliedRigidMaterial,
      appliedCableMaterial,
      appliedEndFittingMaterial,
      appliedStressJointMaterial,
    };
  }

  intervalVersusSegmentOverlap(
    intervalBeg: number,
    intervalEnd: number,
    segStart: number,
    segEnd: number,
    intervalIndex: number,
  ): boolean {
    let begIsContainedBySeg = intervalBeg < segEnd && intervalBeg >= segStart;
    let endIsContainedBySeg = intervalEnd > segStart && intervalEnd <= segEnd;
    const isInvalidPoint = intervalBeg === intervalEnd && intervalBeg === segEnd;
    const segIsContainedByInterval = intervalBeg <= segStart && intervalEnd >= segEnd;

    if (isInvalidPoint) {
      begIsContainedBySeg = false;
      endIsContainedBySeg = false;
    }

    if (intervalIndex === 0) {
      begIsContainedBySeg = intervalBeg <= segEnd && intervalBeg >= segStart;
      endIsContainedBySeg = intervalEnd > segStart && intervalEnd <= segEnd;
    }

    return begIsContainedBySeg || endIsContainedBySeg || segIsContainedByInterval;
  }

  getSelectedPipes(
    beginning: number,
    end: number,
    line: Line,
    intervalIndex: number,
    isConnector = false,
    connectorVersusSegmentOverlap?: ConnectorMatcher,
  ): MatchingPipes {
    if (!line) {
      return { flexible: [], rigid: [], cable: [], endFitting: [], stressJoint: [] };
    }

    const flexible = [];
    const rigid = [];
    const cable = [];
    const endFitting = [];
    const stressJoint = [];
    let currSegStart = 0;

    for (let index = 0; index < line.segmentMesh.length; index++) {
      const {
        selected,
        currentSelectedFlex,
        currentSelectedRigid,
        currentSelectedCable,
        currentSelectedEndFitting,
        currentSelectedStressJoint,
        length,
        appliedFlexMaterial,
        appliedRigidMaterial,
        appliedCableMaterial,
        appliedEndFittingMaterial,
        appliedStressJointMaterial,
      } = this.getSelectedPipesByMeshIndex(
        line,
        index,
        currSegStart,
        beginning,
        end,
        intervalIndex,
        isConnector,
        connectorVersusSegmentOverlap,
      );
      currSegStart += length;

      if (!selected) {
        continue;
      }

      if (currentSelectedFlex && appliedFlexMaterial) {
        flexible.push(appliedFlexMaterial);
      }
      if (currentSelectedRigid && appliedRigidMaterial) {
        rigid.push(appliedRigidMaterial);
      }
      if (currentSelectedCable && appliedCableMaterial) {
        cable.push(appliedCableMaterial);
      }
      if (currentSelectedEndFitting && appliedEndFittingMaterial) {
        endFitting.push(appliedEndFittingMaterial);
      }
      if (currentSelectedStressJoint && appliedStressJointMaterial) {
        stressJoint.push(appliedStressJointMaterial);
      }
    }
    return { flexible, rigid, cable, endFitting, stressJoint };
  }

  checkFlexTypeBySegIndex(index: number, line: Line): Material | null {
    const materialId = line.segmentMaterialId[index];
    const materialType = this.mapsService.getTypeById(materialId);
    const flexMaterial = this.mapsService.getObjectById(materialId);
    return this.isFlexMaterial(materialType) ? flexMaterial : null;
  }

  checkRigidTypeBySegIndex(index: number, line: Line): Material | null {
    const materialId = line.segmentMaterialId[index];
    const materialType = this.mapsService.getTypeById(materialId);
    const rigidMaterial = this.mapsService.getObjectById(materialId);
    return this.isRigidMaterial(materialType) ? rigidMaterial : null;
  }

  checkCableTypeBySegIndex(index: number, line: Line): Material | null {
    const materialId = line.segmentMaterialId[index];
    const materialType = this.mapsService.getTypeById(materialId);
    const cableMaterial = this.mapsService.getObjectById(materialId);
    return this.isCableMaterial(materialType) ? cableMaterial : null;
  }

  checkEndFittingTypeBySegIndex(index: number, line: Line): Material | null {
    const materialId = line.segmentMaterialId[index];
    const materialType = this.mapsService.getTypeById(materialId);
    const endFittingMaterial = this.mapsService.getObjectById(materialId);
    return this.isEndFittingMaterial(materialType) ? endFittingMaterial : null;
  }

  checkStressJointTypeBySegIndex(index: number, line: Line): Material | null {
    const materialId = line.segmentMaterialId[index];
    const materialType = this.mapsService.getTypeById(materialId);
    const stressJointMaterial = this.mapsService.getObjectById(materialId);
    return this.isStressJointMaterial(materialType) ? stressJointMaterial : null;
  }

  isRigidMaterial(materialType: string): boolean {
    return (
      materialType === CONSTANTS.MATERIAL_TYPES.RIGID_TUBE ||
      materialType === CONSTANTS.MATERIAL_TYPES.STRESS_JOINT
    );
  }

  isCableMaterial(materialType: string): boolean {
    return materialType === CONSTANTS.MATERIAL_TYPES.CABLE;
  }

  isFlexMaterial(materialType: string): boolean {
    return materialType === CONSTANTS.MATERIAL_TYPES.FLEX_LINE;
  }

  isEndFittingMaterial(materialType: string): boolean {
    return materialType === CONSTANTS.MATERIAL_TYPES.CONNECTOR;
  }

  isStressJointMaterial(materialType: string): boolean {
    return materialType === CONSTANTS.MATERIAL_TYPES.STRESS_JOINT;
  }
}
